import { MouseEvent } from 'react';
import { DeleteOutline, FormatListBulleted } from '@mui/icons-material';
import { Secret } from 'api/model';
import ScopeIndicator from 'components/indicators/ScopeIndicator';
import ConfirmationDialog from 'components/dialog/ConfirmationDialog';
import useDialog from 'hooks/useDialog';
import useSecret from 'api/secrets/useSecret';
import { Typography } from '@mui/material';
import SecretIcon from './SecretIcon';
import SecretUsageDialog from 'components/dialog/SecretUsageDialog';
import { ActionIconButton } from 'components/button/ActionIconButton';
import { EntityCard } from 'components/settingsHub/EntityCard';
import { FONT_MONO } from 'themes/tokens';

type Props = {
  secret: Secret;
  onSelect: () => void;
};

const SecretItem = ({ secret, onSelect }: Props) => {
  const { remove } = useSecret({ secretId: secret.id, secret });
  const [showDeleteDialog, DeleteDialog] = useDialog(ConfirmationDialog);
  const [showUsageDialog, UsageDialog] = useDialog(SecretUsageDialog);

  const maskedValue =
    secret.value.length > 24 ? `${secret.value.slice(0, 12)}${secret.value.slice(-12)}` : secret.value;

  const stopPropagation = (fn: () => void) => (e: MouseEvent) => {
    e.stopPropagation();
    fn();
  };

  return (
    <EntityCard onClick={onSelect} ariaLabel={`Edit secret ${secret.name}`} minHeight={224}>
      <ScopeIndicator isGlobal={!secret.project_id} className="absolute left-3 top-3 z-10" />
      <div className="entity-card-actions absolute top-2 right-2 z-10 flex gap-1 opacity-0 transition-opacity">
        <ActionIconButton
          size="small"
          color="info"
          title="View usage"
          onClick={stopPropagation(() => showUsageDialog(true))}
        >
          <FormatListBulleted fontSize="small" />
        </ActionIconButton>
        <ActionIconButton
          size="small"
          color="error"
          title="Delete"
          onClick={stopPropagation(() => showDeleteDialog(true))}
        >
          <DeleteOutline fontSize="small" />
        </ActionIconButton>
      </div>

      <div className="flex h-full flex-col items-center justify-evenly gap-2 py-3 text-center">
        <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }} className="line-clamp-2 px-3">
          {secret.name}
        </Typography>
        <SecretIcon type={secret.type} includeText scale={1.15} />
        {secret.description && (
          <Typography variant="body2" color="text.secondary" className="line-clamp-2 px-3">
            {secret.description}
          </Typography>
        )}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontFamily: FONT_MONO }}
          className="max-w-full truncate px-3"
        >
          {maskedValue}
        </Typography>
      </div>

      <DeleteDialog
        title="Delete Secret"
        onConfirm={() => remove.mutate()}
        buttonsSettings={{ confirmText: 'Delete', confirmColor: 'error' }}
      >
        <Typography>
          Are you sure you want to delete the secret <strong>{secret.name}</strong>? This action cannot be undone.
        </Typography>
      </DeleteDialog>
      <UsageDialog secret={secret} onClose={() => showUsageDialog(false)} />
    </EntityCard>
  );
};

export default SecretItem;
